import { z } from "zod";
import {
  adContextSchema,
  saleSourceSchema,
  storeCurrencySchema,
  vendorSelectionSchema,
  answersSchema,
  destinationSchema,
} from "@libs/platform-wire";
export {
  buyerFieldIds,
  buyerGenders,
  buyerTitles,
  buyerPatchSchema,
  savedBuyerSchema,
} from "@libs/identity-onboarding-contracts/buyer";
export { IdentityError as JourneyError } from "@libs/identity-onboarding-contracts/card";
export type {
  Buyer,
  BuyerField,
  BuyerPatch,
  BuyerIdentity,
} from "@libs/identity-onboarding-contracts/buyer";
export type { CardSummary } from "@libs/identity-onboarding-contracts/card";
import type { AttemptAction, AttemptScreen } from "@libs/platform-wire/purchase";
/** A buyer's phone and region as a vendor's checkout types them. */
export { nationalPhone, phoneParts, readPhone, type DialledPhone } from "@libs/countries-contracts/world";
export { regionName } from "@libs/identity-onboarding-contracts/requirements";
/** The buyer's details as the identity reads them, for the server's
 *  purchase compilers (LIS-1173). */
export {
  defaultBuyerCountry,
  parseBuyerDetails,
  prefillBuyerDetails,
  type BuyerFieldDefinition,
  type BuyerRequirements,
} from "@libs/identity-onboarding-contracts/requirements";
export { buyerInCountry, nextBuyerDetails } from "@libs/identity-onboarding-contracts/profile";

export {
  buyerFullName,
  closedPhases,
  deliveryOptionSchema,
  phaseSchema,
  streetAddress,
  terminalPhases,
  totalAnswerSchema,
  vendorCodeAnswerSchema,
  vendorCodeChannels,
  vendorCodePurposes,
  vendorCodeSenders,
  type DeliveryOption,
  type Milestone,
  type Phase,
  type TotalAnswer,
  type TotalPrompt,
  type VendorCodeAnswer,
  type VendorCodeChannel,
  type VendorCodePrompt,
  type VendorCodePurpose,
  type VendorCodeSender,
} from "@libs/buy-on-behalf-contracts/attempt";
import { vendorCodeChannels, type DeliveryOption } from "@libs/buy-on-behalf-contracts/attempt";

/** A listing's id, as a journey names it (`vendor_events.id`). */
export const listingIdSchema = z.string().min(1).max(100);

/** A country, as ISO 3166-1 alpha-2 in capitals. */
export const countryCodeSchema = z.string().regex(/^[A-Z]{2}$/);

/** Ask the store, through the server, what delivering this basket
 *  of the purchase's listing to `destination` costs (LIS-1173, the purchase
 *  design's §2.7): the country, postcode and region the buyer has given so
 *  far, nothing more of their address. `sheetId` names the purchase sheet
 *  (the client's own id for it): a sheet opened before sign-in is answered
 *  too. */
export const destinationEstimateRequestSchema = z
  .object({
    accessId: z.string().min(1).max(100),
    sheetId: z.uuid(),
    listingId: listingIdSchema,
    lines: vendorSelectionSchema,
    destination: destinationSchema,
  })
  .strict();
export type DestinationEstimateRequest = z.infer<typeof destinationEstimateRequestSchema>;

/**
 * The store's answer for one basket. `pending` until it comes; then `ready`
 * with every option it offers (cheapest first), `short` when it would not
 * add as many of a line as asked (`stock`: how many of each line it holds,
 * "Only N left"), `undeliverable` when it offers no delivery for the basket
 * (its carrier takes no parcel that big), or `failed` with the store's own
 * words where it gave any (`message`), to ask again.
 */
export type DeliveryEstimate = {
  id: string;
  accessId: string;
  lines: { item_id: string; quantity: number }[];
  state: "pending" | "ready" | "short" | "undeliverable" | "failed";
  options: DeliveryOption[];
  stock: { item_id: string; available: number }[];
  message: string | null;
};

/** The option the buyer chose from an estimate, as a start pins it. */
export const chosenDeliverySchema = z
  .object({ estimateId: z.uuid(), sheetId: z.uuid(), code: z.string().min(1).max(200) })
  .strict();
export type ChosenDelivery = z.infer<typeof chosenDeliverySchema>;

/** Where a start comes from: an artist's live link, by its slug and the
 *  page it was bought on, or the Lissen app and its platform. The attempt
 *  keeps it. */
export const answeredOriginSchema = z.discriminatedUnion("source", [
  z
    .object({
      source: z.literal("live_link"),
      slug: z.string().min(1).max(160),
      surface: z.enum(["live_link", "site"]),
    })
    .strict(),
  z.object({ source: z.literal("lissen_app"), os: z.enum(["ios", "android", "web"]) }).strict(),
]);
export type AnsweredOrigin = z.infer<typeof answeredOriginSchema>;

/**
 * A start with the buyer's answers to the purchase's fields (LIS-1173, the
 * purchase design's §2.5): the listing the sheet read, its lines and the
 * total and currency it was quoted at, the answers, the delivery option
 * chosen from the store's estimate, the card (null for a free order), and
 * where it came from. The server builds the purchase's fields again, reads
 * the answers with the same function, saves what they change on the
 * buyer's identity and starts with them. A start without them is refused.
 */
export const answeredStartSchema = z
  .object({
    id: z.uuid(),
    accessId: z.string().min(1).max(100),
    listingId: listingIdSchema,
    lines: vendorSelectionSchema,
    answers: answersSchema,
    totalMinor: z.number().int().nonnegative(),
    /** The tax the checkout showed on top of `totalMinor` ("+ £0.20 local
     *  tax", the purchase's `taxRateBps`, LIS-1194): the seller's total then
     *  goes ahead up to a tenth more than the two, and a start whose tax is
     *  not the server's is refused, as one whose total is not. Absent where
     *  it showed "+ local tax": the buyer confirms the seller's total. */
    taxMinor: z.number().int().nonnegative().optional(),
    currency: storeCurrencySchema,
    delivery: chosenDeliverySchema.optional(),
    // Null for a free order, which takes no card.
    cardId: z.string().max(100).nullable(),
    origin: answeredOriginSchema,
    // The client's purchase funnel (LIS-1110): a client-made id, so one
    // that does not parse is a bug, not data.
    funnelId: z.uuid(),
    // Third-party reporting data, which never refuses a purchase: one that
    // does not parse is dropped (and reported, `reportDroppedFields`).
    adContext: adContextSchema.optional().catch(undefined),
    saleSource: saleSourceSchema.optional().catch(undefined),
    /** The purchase the tickets sheet opened (the spine's, warming since Get
     *  tickets): Buy authorises it, with the sheet's capability, instead of
     *  starting one. `id` is its id. `pickVersion` is the pick it was quoted
     *  (`picked`'s answer): an old Buy never authorises a new pick. */
    purchase: z
      .object({
        attachmentId: z.uuid(),
        secret: z.string().min(20).max(200),
        pickVersion: z.number().int().positive().nullable(),
      })
      .strict()
      .optional(),
  })
  .strict();
export type AnsweredStart = z.infer<typeof answeredStartSchema>;

/** A bundle's Buy (LIS-1250): the bundle, and its ticket's and its merch's
 *  starts, each the one its own sheet would send; the ticket's on the
 *  purchase its sheet opened (`purchase`). */
export const bundleStartSchema = z
  .object({ bundleId: z.uuid(), event: answeredStartSchema, merch: answeredStartSchema })
  .strict();
export type BundleStart = z.infer<typeof bundleStartSchema>;

/** The tickets sheet's hold on the purchase it opened (spec v4 §1): the
 *  purchase, its tab and the tab's secret. */
export const purchaseCapabilitySchema = z
  .object({ purchaseId: z.uuid(), attachmentId: z.uuid(), secret: z.string().min(20).max(200) })
  .strict();
export type PurchaseCapability = z.infer<typeof purchaseCapabilitySchema>;

/** `sheet.opened`: the fan pressed Get tickets or Register on a listing.
 *  `pageOpenedAt`: when the event page the sheet is on opened, kept as the
 *  fan's first action in the purchase. */
export const purchaseOpenSchema = z
  .object({
    visitorId: z.string().min(8).max(100), accessId: z.string().min(1).max(100), listingId: listingIdSchema,
    pageOpenedAt: z.iso.datetime().optional(),
  })
  .strict();

/** What `sheet.opened` answers: the purchase it opened, or the runner's
 *  way (the seller is not on the spine), where the sheet starts the
 *  purchase at Buy as always. */
export type PurchaseOpened =
  | { engine: "runner" }
  | ({ engine: "spine" } & PurchaseCapability);

/** The sheet's signals (spec v4 §2), each with its capability. `answer`
 *  and `resend` answer the purchase's ask before Buy (its seller's code),
 *  naming it (`promptId`): an answer to any other is refused. */
export const purchaseSignalSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("picked"), lines: vendorSelectionSchema, country: countryCodeSchema.optional() }).strict(),
  z.object({ kind: z.literal("answer"), promptId: z.uuid(), answers: answersSchema }).strict(),
  z.object({ kind: z.literal("resend"), promptId: z.uuid(), channel: z.enum(vendorCodeChannels) }).strict(),
  z.object({ kind: z.literal("field"), name: z.string().min(1).max(40), value: z.string().max(200) }).strict(),
  z.object({ kind: z.literal("card"), cardId: z.string().min(1).max(100) }).strict(),
  z.object({ kind: z.literal("emailConfirmed") }).strict(),
  z.object({ kind: z.literal("stop") }).strict(),
  z.object({ kind: z.literal("sheet.closed") }).strict(),
  z.object({ kind: z.literal("heartbeat") }).strict(),
]);
export type PurchaseSignal = z.infer<typeof purchaseSignalSchema>;

/** A signal's answer: the input's version it wrote (Buy names the pick's),
 *  the capability to use from now (binding may return the fan's older
 *  purchase), and whether the purchase is still open. */
export type PurchaseSignalled = {
  version?: number;
  capability?: PurchaseCapability;
  open: boolean;
  /** Its ending, once it ended before Buy: a refusal the sheet shows in its
   *  own words (spine v4 §6). */
  ending?: { kind: string; reason: string | null } | null;
  /** What the purchase asks the fan now (its seller's code), with the beat's
   *  and an answer's answer; null when it asks nothing; absent from an older
   *  server. */
  ask?: PurchaseAsk | null;
};

/** A question the purchase asks the fan before Buy (spine v4 §4.2): the
 *  seller's code, drawn as the attempt view's `ask` screen (its `sequence`
 *  is the ask's number), with its resend actions. An answer names
 *  `promptId`. */
export type PurchaseAsk = {
  promptId: string;
  screen: Extract<AttemptScreen, { kind: "ask" }>;
  actions: AttemptAction[];
};

/**
 * The purchase's `prepare` (LIS-1173, the purchase design's §2.5): the
 * buyer's answers to the first and last name fields of the listing the sheet
 * read, sent ahead of Pay to open their agent-mail vendor account early. The
 * server reads them with `readAnswers` against those two fields of the
 * listing's purchase; nothing is saved.
 */
export const answeredPrepareSchema = z
  .object({
    accessId: z.string().min(1).max(100),
    listingId: listingIdSchema,
    answers: answersSchema,
  })
  .strict();
export type AnsweredPrepare = z.infer<typeof answeredPrepareSchema>;
